import { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import classnames from "classnames"
import { useListParams } from "hooks/useListParams"
import {
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap"

import DataCard from "components/Common/DataCard"
import PageWrapper from "components/Common/PageWrapper"
import { prepareDataWithLoading } from "helpers/loading_helper"
import {
  formatCustomerTypeLabel,
  getCustomerDepartmentText,
  getCustomerOrganizationText,
} from "helpers/customerOnboarding"
import { useCustomer } from "hooks/useCustomer"
import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"
import MoneyReceiptsTab from "./MoneyReceiptsTab"
import QuotationsTab from "./QuotationsTab"
import PreOrdersTab from "./PreOrdersTab"
import ActivityTab from "./ActivityTab"
import CustomerWalletTab from "./CustomerWalletTab"
import VehicleMovementsTab from "./VehicleMovementsTab"
import LoanSummaryCard from "pages/AccountPages/AccountLoan/LoanSummaryCard"
import { ProfileCard } from "./Profile"
import ProfileEdit from "./ProfileEdit/ProfileEdit"

const getText = value => {
  if (value === null || value === undefined || value === "") return "-"
  if (typeof value === "object" && value?.name) return value.name
  return String(value)
}

const CustomerProfile = () => {
  const { identifier } = useParams()
  const { data, isLoading, refetch } = useCustomer(identifier)
  const customer = useMemo(() => data?.data ?? null, [data])
  const { hasAnyPermission } = useAuth()
  const canViewActivity = hasAnyPermission([ACCESS.ACTIVITY_LOG.VIEW_LIST])
  const canViewVehicleMovements = hasAnyPermission([
    ACCESS.VEHICLE_MOVEMENT.VIEW_LIST,
  ])
  const canViewLoans = hasAnyPermission([ACCESS.ACCOUNT_LOAN.VIEW_LIST])
  const canViewPreOrders = hasAnyPermission([ACCESS.PRE_ORDER.VIEW_LIST])
  const { params, updateParams } = useListParams({ tab: "money-receipts" })
  const [activeTab, setActiveTab] = useState(params?.tab || "money-receipts")

  document.title = `Customer Profile${
    customer?.name ? ` | ${customer.name}` : ""
  }`

  const toggleTab = tab => {
    updateParams({ tab })
    if (activeTab !== tab) setActiveTab(tab)
  }

  useEffect(() => {
    if (params?.tab && params.tab !== activeTab) setActiveTab(params.tab)
  }, [params?.tab])

  const overviewInfo = prepareDataWithLoading(
    [
      { label: "Customer ID", value: customer?.cuid },
      {
        label: "Customer Type",
        value: formatCustomerTypeLabel(customer?.customer_type) || "-",
      },
      { label: "Name", value: customer?.name },
      { label: "Mobile", value: customer?.mobile },
      { label: "Email", value: customer?.email },
    ],
    isLoading
  )

  const organizationInfo = prepareDataWithLoading(
    [
      { label: "Designation", value: getText(customer?.designation) },
      { label: "Branch", value: getCustomerDepartmentText(customer) || "-" },
      { label: "Organization", value: getCustomerOrganizationText(customer) || "-" },
    ],
    isLoading
  )

  const locationInfo = prepareDataWithLoading(
    [
      { label: "Full Address", value: customer?.full_address || "-" },
    ],
    isLoading
  )

  const activityInfo = prepareDataWithLoading(
    [
      { label: "Total Quotations", value: customer?.quotations?.length ?? 0 },
      { label: "Total Money Receipts", value: customer?.money_receipts?.length ?? 0 },
      { label: "Latest Quotation", value: customer?.quotations?.[0]?.qid || "-" },
      { label: "Latest Receipt", value: customer?.money_receipts?.[0]?.mr_no || "-" },
    ],
    isLoading
  )

  return (
    <PageWrapper
      title="Customer Profile"
      breadcrumbItem={customer?.name || identifier || "Profile"}
      cardBody={false}
    >
      <Row>
        <Col lg="4" className="mb-4">
          <ProfileCard
            loading={isLoading}
            data={customer}
            onCustomer={
              customer ? (
                <ProfileEdit
                  data={customer}
                  onCustomer={() => {
                    refetch()
                  }}
                />
              ) : null
            }
          />
        </Col>

        <Col lg="8">
          <Nav pills className="nav-pills-custom mb-4">
            {[
              {
                id: "money-receipts",
                label: "Money Receipts",
                icon: "bx bx-receipt",
                count: customer?.money_receipts?.length ?? 0,
              },
              {
                id: "quotations",
                label: "Quotations",
                icon: "bx bx-file",
                count: customer?.quotations?.length ?? 0,
              },
              ...(canViewPreOrders
                ? [
                    {
                      id: "pre-orders",
                      label: "Pre-Orders",
                      icon: "bx bx-calendar-check",
                      count: customer?.pre_orders?.length ?? 0,
                    },
                  ]
                : []),
              ...(canViewVehicleMovements
                ? [
                    {
                      id: "vehicle-movements",
                      label: "Vehicle Movements",
                      icon: "bx bx-car",
                      count: customer?.vehicle_movements?.length ?? 0,
                    },
                  ]
                : []),
              { id: "wallet", label: "Wallet & Dues", icon: "bx bx-wallet" },
              ...(canViewLoans
                ? [{ id: "loans", label: "Loans", icon: "bx bx-money" }]
                : []),
              { id: "overview", label: "Overview", icon: "bx bx-grid-alt" },
              ...(canViewActivity
                ? [
                    {
                      id: "activity",
                      label: "Activity",
                      icon: "bx bx-history",
                    },
                  ]
                : []),
            ].map(tab => (
              <NavItem key={tab.id}>
                <NavLink
                  className={classnames({ active: activeTab === tab.id })}
                  onClick={() => toggleTab(tab.id)}
                >
                  <i className={`${tab.icon} me-2`}></i>
                  {tab.label}
                  {typeof tab.count === "number" ? ` (${tab.count})` : ""}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="overview">
              <Row>
                <Col md="6" className="mb-4">
                  <DataCard
                    title="Customer Overview"
                    icon="bx bx-user-circle"
                    data={overviewInfo}
                  />
                </Col>
                <Col md="6" className="mb-4">
                  <DataCard
                    title="Organization Details"
                    icon="bx bx-briefcase-alt-2"
                    data={organizationInfo}
                  />
                </Col>
                <Col md="6" className="mb-4">
                  <DataCard
                    title="Location Details"
                    icon="bx bx-map"
                    data={locationInfo}
                  />
                </Col>
                <Col md="6" className="mb-4">
                  <DataCard
                    title="Activity Summary"
                    icon="bx bx-line-chart"
                    data={activityInfo}
                  />
                </Col>
              </Row>
            </TabPane>

            <TabPane tabId="wallet">
              {activeTab === "wallet" ? (
                <CustomerWalletTab customer={customer || {}} />
              ) : null}
            </TabPane>

            {canViewLoans ? (
              <TabPane tabId="loans">
                {activeTab === "loans" && customer?.id ? (
                  <LoanSummaryCard borrowerType="customer" borrowerId={customer.id} />
                ) : null}
              </TabPane>
            ) : null}

            <TabPane tabId="quotations">
              <QuotationsTab customer={customer || {}} onChanged={refetch} />
            </TabPane>

            {canViewPreOrders ? (
              <TabPane tabId="pre-orders">
                {activeTab === "pre-orders" ? (
                  <PreOrdersTab
                    customer={customer || {}}
                    onChanged={refetch}
                  />
                ) : null}
              </TabPane>
            ) : null}

            <TabPane tabId="money-receipts">
              <MoneyReceiptsTab
                customer={customer || {}}
                onChanged={refetch}
              />
            </TabPane>

            {canViewVehicleMovements ? (
              <TabPane tabId="vehicle-movements">
                {activeTab === "vehicle-movements" ? (
                  <VehicleMovementsTab customer={customer || {}} />
                ) : null}
              </TabPane>
            ) : null}

            {canViewActivity ? (
              <TabPane tabId="activity">
                <ActivityTab customer={customer || {}} />
              </TabPane>
            ) : null}
          </TabContent>
        </Col>
      </Row>
    </PageWrapper>
  )
}

export default CustomerProfile
